import React, { useState, useRef, useEffect } from "react";
import { Search, Download, Eye, X, FileText } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  useGetRepairReportsQuery,
  useGetRepairRequestQuery,
  useUpdateScheduleMutation,
  useLazyGetScheduleByRepairIDQuery,
} from "../../slices/maintenanceApiSlice";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  useGetUserByEmailQuery,
  useGetUsersQuery,
  useGetAcademyQuery,
  useGetDepartmentQuery,
} from "../../slices/userApiSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

const Repairreport = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [editableData, setEditableData] = useState({
    Additional_cost: "",
    Additional_Hour: "",
    Remarks: "",
  });

  const rowsPerPage = 10;

  const { data: repairRequest, refetch: refetchRepairRequest } =
    useGetRepairRequestQuery();
  const { data: repairReport, isLoading, refetch } = useGetRepairReportsQuery();
  const { data: academy } = useGetAcademyQuery();

  const [updateSchedule] = useUpdateScheduleMutation();
  const [fetchSchedulesByRepairID] = useLazyGetScheduleByRepairIDQuery();
  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || "",
  );
  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email);

  const { data: users } = useGetUsersQuery();
  const [data, setData] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (repairReport && repairRequest && academy && users && userByEmial) {
      const loginAcademyId = userByEmial.user.academyId?.trim().toLowerCase();

      const fetchAndMerge = async () => {
        const mergedDataPromises = repairReport.map(async (report) => {
          const matchingRequest = repairRequest.find(
            (request) =>
              request.repairID === report.repairID &&
              request.status !== "In Progress",
          );

          if (!matchingRequest) return null;

          const requestAcademyId = matchingRequest.academyId
            ?.trim()
            .toLowerCase();

          if (requestAcademyId !== loginAcademyId) return null;

          let scheduleData = null;
          try {
            scheduleData = await fetchSchedulesByRepairID(
              report.repairID,
            ).unwrap();
          } catch (error) {
            console.error(
              `Failed to fetch schedule for repairID ${report.repairID}`,
              error,
            );
          }

          const schedule = Array.isArray(scheduleData)
            ? scheduleData[0]
            : scheduleData;

          let total = 0;
          if (report.technicians) {
            const technicianList = report.technicians
              .split(",")
              .filter((email) => email.trim() !== "");
            total = technicianList.length;
          }

          const matchingAcademy = academy.find(
            (a) => a.academyId?.trim().toLowerCase() === requestAcademyId,
          );

          return {
            ...report,
            academyId: matchingRequest.academyId || null,
            academyName: matchingAcademy?.name || "Unknown Academy",
            assetName: matchingRequest.assetName || report.assetName || "N/A",
            area: matchingRequest.area || "N/A",
            description: matchingRequest.description || "N/A",
            totalTechnicians: total,
            addCost: schedule?.addCost || "",
            addHours: schedule?.addHours || "",
            remark: schedule?.remark || "",
          };
        });

        const mergedData = (await Promise.all(mergedDataPromises)).filter(
          Boolean,
        );

        const sortedFiltered = mergedData.sort((a, b) =>
          b.repairReportID.localeCompare(a.repairReportID),
        );

        setData(sortedFiltered);
      };

      fetchAndMerge();
    }
  }, [
    repairReport,
    repairRequest,
    academy,
    users,
    userByEmial,
    fetchSchedulesByRepairID,
  ]);

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading repair reports...", {
        id: "loading-repair-reports",
        duration: Infinity,
      });
    } else {
      toast.dismiss("loading-repair-reports");
    }
  }, [isLoading]);

  const handleUpdate = async () => {
    const repairID = modalData?.repairID;

    if (!repairID) {
      toast.error("Missing Repair ID", {
        description:
          "The repair ID is missing. Please try again or contact support if the issue persists.",
      });
      return;
    }

    if (
      editableData.Additional_cost.trim() === "" ||
      editableData.Additional_Hour.trim() === "" ||
      editableData.Remarks.trim() === ""
    ) {
      toast.warning("Incomplete Information", {
        description: "Please fill in all fields before saving.",
      });
      return;
    }

    const additionalCost = parseFloat(editableData.Additional_cost);
    const additionalHours = parseFloat(editableData.Additional_Hour);

    if (
      isNaN(additionalCost) ||
      isNaN(additionalHours) ||
      additionalCost < 0 ||
      additionalHours < 0
    ) {
      toast.warning("Negative Values", {
        description: "Cost and hours cannot be negative.",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const scheduleRes = await fetchSchedulesByRepairID(repairID).unwrap();

      const scheduleID = Array.isArray(scheduleRes)
        ? scheduleRes[0]?.scheduleID
        : scheduleRes?.scheduleID;

      if (!scheduleID) {
        toast.error("Schedule Not Found", {
          description: "Unable to fetch a schedule for this repair ID.",
        });
        return;
      }

      const updatedSchedule = {
        addCost: editableData.Additional_cost,
        addHours: editableData.Additional_Hour,
        remark: editableData.Remarks,
      };
      await updateSchedule({
        scheduleID,
        updatedSchedule,
      }).unwrap();

      toast.success("Success", {
        description: "Labor cost and hours updated successfully.",
      });
      setEditableData({
        Additional_cost: "",
        Additional_Hour: "",
        Remarks: "",
      });
      refetch();
      refetchRepairRequest();
      setModalData(null);
    } catch (error) {
      toast.error("Error", {
        description: "Failed to update schedule.",
      });
    }
    setIsUpdating(false);
  };

  const sortedData = [...data].sort(
    (a, b) => b.repairReportID - a.repairReportID,
  );

  const filteredData = sortedData.filter((item) => {
    const matchesSearch = Object.values(item).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    );
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleSelectRow = (repairReportID) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(repairReportID)
        ? prevSelectedRows.filter((item) => item !== repairReportID)
        : [...prevSelectedRows, repairReportID],
    );
  };

  const handleView = (item) => {
    setModalData(item);
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateTotalLaborHours = (startTime, endTime, numTechnicians) => {
    if (!startTime || !endTime) return "N/A";

    const parseTime = (timeStr) => {
      if (typeof timeStr !== 'string') return { hours: 0, minutes: 0 };
      const parts = timeStr.trim().split(" ");
      if (parts.length === 0) return { hours: 0, minutes: 0 };

      const time = parts[0];
      const modifier = parts[1] || "";

      let [hours, minutes] = time.split(":").map(Number);
      if (isNaN(hours)) hours = 0;
      if (isNaN(minutes)) minutes = 0;

      if (modifier.toUpperCase() === "PM" && hours < 12) hours += 12;
      if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;

      return { hours, minutes };
    };

    try {
      const start = parseTime(startTime);
      const end = parseTime(endTime);

      let diffHours = end.hours - start.hours;
      let diffMins = end.minutes - start.minutes;

      if (diffMins < 0) {
        diffHours -= 1;
        diffMins += 60;
      }

      if (diffHours < 0) {
        diffHours += 24;
      }

      const totalTimeHours = diffHours + (diffMins / 60);
      const laborHours = totalTimeHours * (numTechnicians || 1);

      if (laborHours >= 1) {
        return `${laborHours.toFixed(2)} hrs`;
      } else if (laborHours > 0 && laborHours < 1) {
        const mins = Math.round(laborHours * 60);
        if (mins >= 1) {
          return `${mins} mins`;
        } else {
          const secs = Math.round(laborHours * 3600);
          return `${secs} secs`;
        }
      } else {
        return "0 hrs";
      }
    } catch (e) {
      return "N/A";
    }
  };

  const handleDownloadPDF = async () => {
    if (!modalData) return;
    toast.loading("Generating PDF...", { id: "pdf-gen" });

    try {
      const doc = new jsPDF();
      doc.text("Repair Report", 14, 15);

      const columns = ["Field", "Value"];
      const rows = [
        ["RID", modalData.repairReportID],
        ["Asset Name", modalData.assetName],
        ["Start Time", modalData.startTime],
        ["End Time", modalData.endTime],
        ["Total Work Hours", calculateTotalLaborHours(modalData.startTime, modalData.endTime, modalData.totalTechnicians)],
        ["Date", modalData.finishedDate],
        ["Area", modalData.area],
        ["Total Cost", modalData.totalCost],
        ["Part Used", modalData.partsUsed],
        ["Description", modalData.description],
        ["Total Technicians", modalData.totalTechnicians],
        ["Assigned Technician", modalData.technicians],
      ];

      autoTable(doc, {
        head: [columns],
        body: rows,
        startY: 20,
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [41, 128, 185] },
      });

      let currentY = doc.lastAutoTable.finalY + 10;
      let images = modalData.images || [];
      if (!Array.isArray(images)) images = [images];
      images = images.filter(Boolean);

      if (images.length > 0) {
        if (currentY > 270) {
          doc.addPage();
          currentY = 20;
        }
        doc.text("Attached Images:", 14, currentY);
        currentY += 10;

        for (const imgSrc of images) {
          try {
            const imgObj = await new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = "Anonymous";
              img.onload = () => resolve(img);
              img.onerror = reject;
              img.src = imgSrc;
            });

            const canvas = document.createElement("canvas");
            canvas.width = imgObj.width;
            canvas.height = imgObj.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(imgObj, 0, 0);
            const imgData = canvas.toDataURL("image/jpeg", 0.7);

            const maxWidth = 100;
            const maxHeight = 100;
            let finalWidth = imgObj.width;
            let finalHeight = imgObj.height;
            const ratio = finalWidth / finalHeight;
            if (finalWidth > maxWidth) {
              finalWidth = maxWidth;
              finalHeight = finalWidth / ratio;
            }
            if (finalHeight > maxHeight) {
              finalHeight = maxHeight;
              finalWidth = finalHeight * ratio;
            }

            if (currentY + finalHeight > 280) {
              doc.addPage();
              currentY = 20;
            }

            doc.addImage(imgData, "JPEG", 14, currentY, finalWidth, finalHeight);
            currentY += finalHeight + 10;
          } catch (e) {
            console.error("Failed to load image for PDF:", e);
          }
        }
      }

      doc.save(`Repair_Report_${modalData.repairReportID}.pdf`);
      toast.success("PDF downloaded successfully", { id: "pdf-gen" });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF", { id: "pdf-gen" });
    }
  };

  const handleDownloadSelected = () => {
    if (selectedRows.length === 0) return;

    const selectedData = data.filter((item) =>
      selectedRows.includes(item.repairReportID),
    );

    const csvContent = [
      [
        "Repair ID",
        "Asset Name",
        "Start Time",
        "End Time",
        "Total Work Hours",
        "Date",
        "Area",
        "Total Cost",
        "Parts Used",
        "Description",
        "Total Technicians",
        "Assigned Technician",
      ],
      ...selectedData.map((item) => [
        item.repairReportID,
        item.assetName,
        item.startTime,
        item.endTime,
        calculateTotalLaborHours(item.startTime, item.endTime, item.totalTechnicians),
        item.finishedDate,
        item.area,
        item.totalCost,
        item.partsUsed,
        item.description,
        item.totalTechnicians,
        item.technicians,
      ]),
    ]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "repair_report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            Repair Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and analyze repair maintenance reports and statistics
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search repair reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Button
            onClick={handleDownloadSelected}
            disabled={selectedRows.length === 0}
            variant="default"
            style={{
              backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
              color: "white",
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Download Selected
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={
                      selectedRows.length === displayedData.length &&
                      displayedData.length > 0
                    }
                    onCheckedChange={() =>
                      setSelectedRows(
                        selectedRows.length === displayedData.length
                          ? []
                          : displayedData.map((item) => item.repairReportID),
                      )
                    }
                  />
                </TableHead>
                <TableHead className="font-semibold">Sl. No.</TableHead>
                <TableHead className="font-semibold">Asset Name</TableHead>
                <TableHead className="font-semibold">Time</TableHead>
                <TableHead className="font-semibold">Work Hours</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Area</TableHead>
                <TableHead className="font-semibold">Total Cost</TableHead>
                <TableHead className="font-semibold">Parts Used</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length > 0 ? (
                displayedData.map((item, index) => (
                  <TableRow key={item.repairReportID || index}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRows.includes(item.repairReportID)}
                        onCheckedChange={() =>
                          handleSelectRow(item.repairReportID)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>{item.assetName || "-"}</TableCell>
                    <TableCell>
                      {[item.startTime, item.endTime].join(" - ")}
                    </TableCell>
                    <TableCell>
                      {calculateTotalLaborHours(item.startTime, item.endTime, item.totalTechnicians)}
                    </TableCell>
                    <TableCell>{formatDate(item.finishedDate)}</TableCell>
                    <TableCell>{item.area || "-"}</TableCell>
                    <TableCell>{item.totalCost || "-"}</TableCell>
                    <TableCell>{item.partsUsed || "-"}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {item.description || "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(item)}
                        style={{
                          backgroundColor:
                            "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                          color: "white",
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No repair reports found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="border-t bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing{" "}
            <span className="font-medium text-foreground">
              {displayedData.length > 0
                ? (currentPage - 1) * rowsPerPage + 1
                : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium text-foreground">
              {Math.min(currentPage * rowsPerPage, filteredData.length)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-foreground">
              {filteredData.length}
            </span>{" "}
            results
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Dialog
        open={!!modalData}
        onOpenChange={(open) => !open && handleCloseModal()}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Repair Report Details</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asset Name</Label>
                <Input value={modalData?.assetName || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Input value={modalData?.finishedDate || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input value={modalData?.startTime || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>End Time</Label>
                <Input value={modalData?.endTime || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Total Labor Hours</Label>
                <Input value={calculateTotalLaborHours(modalData?.startTime, modalData?.endTime, modalData?.totalTechnicians)} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Area</Label>
                <Input value={modalData?.area || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Total Cost</Label>
                <Input value={modalData?.totalCost || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Parts Used</Label>
                <Input value={modalData?.partsUsed || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Total Technicians</Label>
                <Input value={modalData?.totalTechnicians || ""} readOnly />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Assigned Technicians</Label>
              <Input value={modalData?.technicians || ""} readOnly />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={modalData?.description || ""}
                readOnly
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Additional Information</Label>
              <Textarea
                value={modalData?.information || ""}
                readOnly
                rows={2}
              />
            </div>

            {modalData?.images && (
              <div className="space-y-2">
                <Label>Repaired Images</Label>
                <div className="grid grid-cols-2 gap-4">
                  {Array.isArray(modalData.images) &&
                    modalData.images.length > 0 ? (
                    modalData.images.map((imgSrc, index) => (
                      <img
                        key={index}
                        src={imgSrc}
                        alt={`Repair ${index + 1}`}
                        className="w-full h-48 object-cover rounded border"
                      />
                    ))
                  ) : modalData.images ? (
                    <img
                      src={modalData.images}
                      alt="Repair"
                      className="w-full h-48 object-cover rounded border"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No image available
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Additional Cost</Label>
                {modalData?.addCost ? (
                  <Input value={modalData.addCost} readOnly />
                ) : (
                  <Input
                    name="Additional_cost"
                    value={editableData.Additional_cost}
                    onChange={handleInputChange}
                    type="number"
                    placeholder="Enter additional cost"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label>Additional Hours</Label>
                {modalData?.addHours ? (
                  <Input value={modalData.addHours} readOnly />
                ) : (
                  <Input
                    name="Additional_Hour"
                    value={editableData.Additional_Hour}
                    onChange={handleInputChange}
                    type="number"
                    placeholder="Enter additional hours"
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              {modalData?.remark ? (
                <Textarea value={modalData.remark} readOnly rows={2} />
              ) : (
                <Textarea
                  name="Remarks"
                  value={editableData.Remarks}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Enter remarks"
                />
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            {!modalData?.addCost &&
              !modalData?.addHours &&
              !modalData?.remark && (
                <Button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  style={{
                    backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                    color: "white",
                  }}
                >
                  {isUpdating ? "Updating..." : "Update"}
                </Button>
              )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Repairreport;
